import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import MOMSection from '../components/dashboard/sections/MOMSection';
import ProposalsSection from '../components/dashboard/sections/ProposalsSection';
import PaymentsSection from '../components/dashboard/sections/PaymentsSection';
import DocumentsSection from '../components/dashboard/sections/DocumentsSection';
import useModuleStats from '../components/dashboard/sections/useModuleStats';
import KpiCard from '../components/dashboard/overview/KpiCard';
import ProjectsOverviewCard from '../components/dashboard/overview/ProjectsOverviewCard';
import TransactionsOverviewCard, { TransactionTotals } from '../components/dashboard/overview/TransactionsOverviewCard';
import { clampPercent, formatCount, formatLongDate } from '../components/dashboard/overview/format';
import '../components/dashboard/dashboard.css';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../services/api/client';

const DashboardAdmin: React.FC = () => {
  const { t, isRTL } = useLanguage();
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const projectStats = useModuleStats<any>('/projects/statistics/');

  useEffect(() => {
    let cancelled = false;
    api.get('/dashboard/admin/')
      .then((res) => { if (!cancelled) setDashboardData(res.data.data || res.data); })
      .catch(() => { if (!cancelled) setDashboardData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const tx: TransactionTotals | null = useMemo(() => {
    if (!dashboardData) return null;
    return {
      total: dashboardData.total_transactions ?? 0,
      active: dashboardData.active_transactions ?? 0,
      late: dashboardData.overdue_transactions ?? 0,
      pendingAssignment: dashboardData.pending_assignments ?? 0,
      completed: dashboardData.completed_transactions ?? 0,
      rate: clampPercent(dashboardData.completion_rate),
    };
  }, [dashboardData]);

  const byStatus: Record<string, number> = projectStats.data?.by_status || {};
  const projectsTotal: number = projectStats.data?.total ?? 0;
  const dash = '—';

  return (
    <Layout>
      <div className="container-fluid">
        <div className="dash-page">
          <div className="dash-page-header">
            <div className="dash-page-title-block">
              <h1 className="dash-page-title">{t('adminDashboard')}</h1>
              <p className="dash-page-subtitle">
                {isRTL ? 'مرحباً بعودتك، إليك ملخص العمليات' : 'Welcome back — here is your operations summary'}
                {' · '}
                {formatLongDate(new Date(), isRTL)}
              </p>
            </div>
            <div className="dash-page-actions">
              <Link to="/reports" className="dash-btn dash-btn-secondary">
                <i className="bi bi-download" aria-hidden="true" />
                {isRTL ? 'تصدير التقرير' : 'Export report'}
              </Link>
              <Link to="/transactions/create" className="dash-btn dash-btn-primary">
                <i className="bi bi-plus-lg" aria-hidden="true" />
                {isRTL ? 'معاملة جديدة' : 'New transaction'}
              </Link>
            </div>
          </div>

          <div className="dash-kpi-grid">
            <KpiCard
              label={isRTL ? 'إجمالي المعاملات' : 'Total transactions'}
              icon="bi-clipboard-check"
              tone="brand"
              loading={loading}
              value={tx ? formatCount(tx.total) : dash}
              sub={tx && (isRTL
                ? `${formatCount(tx.active)} نشطة · ${formatCount(tx.late)} متأخرة`
                : `${formatCount(tx.active)} active · ${formatCount(tx.late)} overdue`)}
            />
            <KpiCard
              label={isRTL ? 'معدل إنجاز المعاملات' : 'Transaction completion'}
              icon="bi-check2-circle"
              tone="green"
              loading={loading}
              value={tx ? `${tx.rate}%` : dash}
              progress={tx?.rate ?? 0}
              progressTone="green"
            />
            <KpiCard
              label={isRTL ? 'المشاريع' : 'Projects'}
              icon="bi-folder2"
              tone="blue"
              loading={projectStats.loading}
              value={projectStats.data ? formatCount(projectsTotal) : dash}
              sub={projectStats.data && (isRTL
                ? `${formatCount(byStatus.active)} نشط · ${formatCount(byStatus.draft)} مسودة · ${formatCount(byStatus.completed)} مكتمل`
                : `${formatCount(byStatus.active)} active · ${formatCount(byStatus.draft)} draft · ${formatCount(byStatus.completed)} completed`)}
            />
            <KpiCard
              label={isRTL ? 'متوسط تقدم المشاريع الجارية' : 'Avg. progress of ongoing projects'}
              icon="bi-graph-up-arrow"
              tone="amber"
              loading={projectStats.loading}
              value={projectStats.data ? `${clampPercent(projectStats.data.average_progress)}%` : dash}
              progress={projectStats.data?.average_progress ?? 0}
            />
          </div>

          <div className="dash-row">
            <ProjectsOverviewCard byStatus={byStatus} total={projectsTotal} loading={projectStats.loading} />
            <TransactionsOverviewCard totals={tx} loading={loading} />
          </div>

          {/* The other modules keep their own sections below the overview
              (admin sees every module — the admin role is unconditional).
              Each hides itself when its module has no data yet. */}
          <div>
            <MOMSection />
            <ProposalsSection />
            <PaymentsSection />
            <DocumentsSection />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardAdmin;
